import {
  Button,
  Divider,
  Flex,
  Form,
  Layout,
  message,
  Radio,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { ALL_PAYMENT_METHOD_VALUES, PAYMENT_INTENT_STATUS } from "common/constants";
import { formatCurrency } from "common/utils";
import currency from "currency.js";
import moment from "moment-timezone";
import { useUserType } from "features/auth";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchCreditorPortalDebtorAccountsQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";
import { getPaymentsScheduleForOneTimePayment } from "features/payments/paymentDefaults";
import { useGetPaymentIntentsQuery } from "features/payments/paymentsAPI";
import {
  getNextView,
  selectPaymentsSlice,
  setAsOneTimePayment,
  setAsPaymentPlan,
  setOneTimePaymentsSchedule,
  setPaidTo,
  setSelectedAccounts,
  setupAsPaymentPlan,
  setIsZeroBalanceAccountVisibleOption,
} from "features/payments/paymentsSlice";
import PaymentConfiguration from "features/payments/wizardSteps/common/paymentConfiguration";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

const BottomSpacedStrong = styled(Layout.Content)`
  font-weight: bold;
  margin-bottom: 8px;
`;

const StyledHeader = styled.h4`
  font-weight: 500;
`;

const StyledAccountsHeader = styled.h4`
  font-weight: 500;
  margin-bottom: 0;
`;

const StyledValue = styled.strong`
  font-weight: 500;
`;

const StyledTable = styled(Table)`
  margin-top: 12px;
  & .ant-table-cell:not(th) {
    padding: 4px 8px !important;
  }
`;

const StyledSwitchLabel = styled.span`
  font-size: 12px;
`;

const StyledAmountDueDiv = styled.div`
  margin-bottom: 8px;
  margin-top: 8px;
`;

const StyledDivider = styled(Divider)`
  margin: 8px 0;
`;

function SelectAccountsForPayment() {
  const { isAgencyUserType, isCreditorUserType } = useUserType();
  const { debtorId } = useParams();
  const dispatch = useDispatch();
  const { data: agencyDebtorAccounts } = useFetchDebtorAccountsQuery(
    {
      debtorId,
      isIncludePacketedAccounts: true,
    },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorAccounts, isLoading } =
    useFetchCreditorPortalDebtorAccountsQuery({ debtorId }, { skip: isAgencyUserType });
  const debtorAccounts = useMemo(() => {
    if (isAgencyUserType) {
      return [...(agencyDebtorAccounts || [])].sort((a, b) => {
        // Sort by most recent followUpDate first; accounts with no follow up date go last.
        if (!a.followUpDate && !b.followUpDate) return 0;
        if (!a.followUpDate) return 1;
        if (!b.followUpDate) return -1;
        return moment(b.followUpDate).diff(moment(a.followUpDate));
      });
    }
    return creditorPortalDebtorAccounts;
  }, [isAgencyUserType, agencyDebtorAccounts, creditorPortalDebtorAccounts]);
  const paymentsSlice = useSelector(selectPaymentsSlice);
  const { data: creditors } = useFetchCreditorSummariesQuery({}, { skip: !isAgencyUserType });
  const debtTotal = Object.values(paymentsSlice.selectedAccounts).reduce(
    (accumulator, selectedAccountId) => {
      const selectedAccount = debtorAccounts?.find(
        (debtorAccount) => debtorAccount.id === selectedAccountId,
      );
      return currency(accumulator, { precision: 4 }).add(selectedAccount?.totalBalance ?? 0);
    },
    0,
  );
  const { data: isPaymentsScheduleAuthorized } = useAuthorization(PERMISSIONS.PAYMENT__SCHEDULE);
  const {
    data: isMultipleAccountsOnPaymentAuthorized,
    isLoading: isMultipleAccountsOnPaymentAuthorizedLoading,
  } = useAuthorization(PERMISSIONS.PAYMENT_SCHEDULE__MULTIPLE_ACCOUNTS_ON_PAYMENT);
  const { data: paymentIntents } = useGetPaymentIntentsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );

  const scheduledPayments = paymentIntents?.filter(
    (paymentIntent) =>
      ALL_PAYMENT_METHOD_VALUES.includes(paymentIntent.paymentMethod.type) &&
      paymentIntent.status === PAYMENT_INTENT_STATUS.NEW &&
      !paymentIntent.isLastTransactionDeclined,
  );

  const columns = [
    {
      title: "Account",
      dataIndex: "externalId",
      render: (text, record) => {
        return (
          <>
            <div>Account #{record.externalId}</div>
            <Space size={1}>
              {scheduledPayments?.find((each) => each.accountIds?.includes(record.id)) ? (
                <Tag color="yellow">PAYMENT(S) SCHEDULED</Tag>
              ) : null}
              {!record.debtors.find((debtor) => debtor.id.toString() === debtorId.toString()) ? (
                <Tag color="blue">PACKETED</Tag>
              ) : null}
            </Space>
          </>
        );
      },
    },
    {
      title: "Status Code",
      dataIndex: "statusCode",
      key: "statusCode",
      render: (text, record) => {
        return record.statusCode || "-";
      },
    },
    {
      title: "Affected Debtors",
      dataIndex: "debtors",
      hidden: !isAgencyUserType,
      render: (text, record) =>
        record.debtors
          ?.map((debtor) =>
            [
              debtor.firstName,
              debtor.middleName,
              debtor.lastName,
              debtor.id.toString() === debtorId.toString()
                ? undefined
                : `(ID: ${debtor.externalId})`,
            ]
              .filter(Boolean)
              .join(" "),
          )
          .join(", ") ?? "-",
    },
    {
      title: "Legal Case #",
      dataIndex: "lawsuitCaseNumber",
      render: (text, record) => record.lawsuitCaseNumber || "-",
      hidden: !isAgencyUserType,
      filters: useMemo(() => {
        if (debtorAccounts) {
          return [
            ...new Set(
              debtorAccounts
                .map((debtorAccount) => debtorAccount.lawsuitCaseNumber)
                .filter((lawsuitCaseNumber) => lawsuitCaseNumber),
            ),
          ].map((lawsuitCaseNumber) => ({
            text: lawsuitCaseNumber,
            value: lawsuitCaseNumber,
          }));
        }
      }, [debtorAccounts]),
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) =>
        record.lawsuitCaseNumber?.toString().toLowerCase().includes(value.toString().toLowerCase()),
      sorter: (a, b) => a?.lawsuitCaseNumber?.localeCompare(b?.lawsuitCaseNumber ?? ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Amount Due",
      dataIndex: "debtAmount",
      render: (text) => (text !== null ? formatCurrency(text) : null),
    },
    {
      title: "Client",
      dataIndex: "creditorName",
      hidden: !isAgencyUserType,
    },
  ].filter((column) => !column.hidden);

  const { isZeroBalanceAccountVisible } = paymentsSlice;

  const data = useMemo(
    () =>
      debtorAccounts
        ?.reduce((acc, debtorAccount) => {
          const debtAmount = currency(debtorAccount.totalBalance, { precision: 4 }).value;
          const creditor = creditors?.find((c) => c.id === debtorAccount.creditorId);
          return [
            ...acc,
            {
              key: debtorAccount.id,
              id: debtorAccount.id,
              externalId: debtorAccount.externalId,
              statusCode: debtorAccount.statusCode,
              creditorName: creditor?.name,
              debtAmount,
              lawsuitCaseNumber: debtorAccount.lawsuitCaseNumber,
              debtors: debtorAccount.debtors,
            },
          ];
        }, [])
        .filter(({ debtAmount }) => isZeroBalanceAccountVisible || debtAmount > 0),
    [creditors, debtorAccounts, isZeroBalanceAccountVisible],
  );

  useEffect(() => {
    // Set default paidTo value based on the user type
    const paidTo = isCreditorUserType ? "creditor" : "agency";
    dispatch(setPaidTo(paidTo));

    if (!isMultipleAccountsOnPaymentAuthorized) {
      // NOTE: This permission is not configurable for admin users due to a legacy hack - see hasPermission in useAuthorization.js
      // When the user has the permission to only select a single account for payments, they cannot select multiple accounts
      // because we force a radio button instead of a checkbox.
      if (data?.length === 1) {
        // Only pre-select if there's only one account.
        dispatch(
          setSelectedAccounts(
            data.filter((account) => account.debtAmount).map((account) => account.key),
          ),
        );
      } else {
        // Do not select any accounts by default if there's more than one account to force
        // the user to select a single account.
        dispatch(setSelectedAccounts([]));
      }
    } else if (data?.length >= 1) {
      // By default, if the user has the permission to select multiple accounts, we pre-select all accounts.
      dispatch(
        setSelectedAccounts(
          data.filter((account) => account.debtAmount).map((account) => account.key),
        ),
      );
    } else {
      // Do not select any accounts by default if there are no accounts to select.
      dispatch(setSelectedAccounts([]));
    }
  }, [dispatch, isCreditorUserType, data, isMultipleAccountsOnPaymentAuthorized]);

  const onSelectChange = (newSelectedRowKeys) => {
    dispatch(setSelectedAccounts(newSelectedRowKeys));
  };

  const rowSelection = {
    selectedRowKeys: paymentsSlice.selectedAccounts,
    onChange: onSelectChange,
  };
  const hasSelected = paymentsSlice.selectedAccounts.length > 0;
  const isSelectionValid =
    hasSelected &&
    (rowSelection.selectedRowKeys.every(
      (selectedAccountId) =>
        data.find((account) => account.id === selectedAccountId)?.debtAmount > 0,
    ) ||
      rowSelection.selectedRowKeys.length === 1);
  const selectionTooltipMessage = hasSelected
    ? "Please select a single account with a zero balance if you wish to make an overpayment"
    : "Select 1 or more accounts to continue";

  const onPaymentTypeChange = (e) => {
    if (e.target.value) {
      dispatch(setAsPaymentPlan());
    } else {
      dispatch(setAsOneTimePayment());
    }
  };

  const onPaidToChange = (e) => {
    dispatch(setPaidTo(e.target.value));
  };

  const onZeroBalanceAccountVisibilityChange = (enabled) => {
    if (!enabled) {
      dispatch(
        setSelectedAccounts(
          rowSelection.selectedRowKeys.filter(
            (selectedAccountId) =>
              data.find((account) => account.id === selectedAccountId)?.debtAmount > 0,
          ),
        ),
      );
    }
    dispatch(setIsZeroBalanceAccountVisibleOption(enabled));
  };

  const [form] = Form.useForm();

  const initialValues = {
    settled: paymentsSlice.isSettlement,
    partialPaymentAmount: paymentsSlice.partialPaymentAmount,
    originalTotalDue: debtTotal,
  };

  const onOneTimePaymentFinish = (values) => {
    const { partialPaymentAmount, isInterestApplicable } = values;
    const partialPaymentAmountValue = currency(partialPaymentAmount);
    const originalTotalAmount = debtTotal;
    let totalAmount;
    if (paymentsSlice.isPartialPayment || paymentsSlice.isSettlement) {
      totalAmount = partialPaymentAmountValue;
    } else {
      totalAmount = originalTotalAmount;
    }

    const paymentsSchedule = getPaymentsScheduleForOneTimePayment({
      accountIds: paymentsSlice.selectedAccounts,
      accountUuids: null,
      paymentAmount: totalAmount.value,
    });
    const downPayment = 0;
    dispatch(
      setOneTimePaymentsSchedule({
        partialPaymentAmount: partialPaymentAmountValue.value,
        originalTotalAmount: originalTotalAmount.value,
        totalAmount: totalAmount.value,
        downPayment,
        paymentsSchedule,
        isInterestApplicable,
      }),
    );
    dispatch(getNextView());
  };

  const onPaymentPlanFinish = (values) => {
    const { partialPaymentAmount, isInterestApplicable } = values;
    const partialPaymentAmountValue = currency(partialPaymentAmount, { precision: 4 });
    let totalAmount = currency(0, { precision: 4 });
    const originalTotalAmount = debtTotal;
    if (paymentsSlice.isPartialPayment || paymentsSlice.isSettlement) {
      totalAmount = partialPaymentAmountValue;
    } else {
      totalAmount = originalTotalAmount;
    }

    dispatch(
      setupAsPaymentPlan({
        partialPaymentAmount: partialPaymentAmountValue.value,
        originalTotalAmount: originalTotalAmount.value,
        totalAmount: totalAmount.value,
        isInterestApplicable,
      }),
    );
    dispatch(getNextView());
  };

  const onFinish = (values) => {
    const selectedAccounts = Object.values(paymentsSlice.selectedAccounts).map(
      (selectedAccountId) => {
        const selectedAccount = debtorAccounts?.find(
          (debtorAccount) => debtorAccount.id === selectedAccountId,
        );
        return selectedAccount;
      },
    );
    if (!isMultipleAccountsOnPaymentAuthorized) {
      // NOTE: Theoretically this should never happen, because it's either a radio button or a checkbox already.
      // But just in case, throw an error if they try to select more than one account.
      if (selectedAccounts.length > 1) {
        message.error("Only one account can be selected when scheduling a payment");
        return;
      }
    }
    values.isInterestApplicable = selectedAccounts.some((account) => account.interestRate > 0);
    if (paymentsSlice.isPaymentPlan) {
      onPaymentPlanFinish(values);
    } else {
      onOneTimePaymentFinish(values);
    }
  };

  return (
    <div>
      <BottomSpacedStrong>Payment Configuration</BottomSpacedStrong>
      <StyledHeader>Paid to</StyledHeader>
      <Radio.Group onChange={onPaidToChange} value={paymentsSlice.paidTo}>
        <Radio value="agency">Agency</Radio>
        <Radio value="creditor">Client</Radio>
        {isAgencyUserType && <Radio value="forwarding_entity">Forwarding Entity</Radio>}
      </Radio.Group>
      <br />
      {isAgencyUserType && paymentsSlice.paidTo === "agency" ? (
        <>
          <StyledHeader>Payment Type</StyledHeader>
          <Radio.Group onChange={onPaymentTypeChange} value={paymentsSlice.isPaymentPlan}>
            <Radio value={false}>One Time Payment</Radio>
            <Radio value disabled={!isPaymentsScheduleAuthorized}>
              Setup a Payment Plan
            </Radio>
          </Radio.Group>
          <br />
        </>
      ) : null}
      <StyledAccountsHeader>Accounts</StyledAccountsHeader>
      <Flex justify="flex-end" align="end">
        <Space>
          <Switch
            checked={isZeroBalanceAccountVisible}
            onChange={onZeroBalanceAccountVisibilityChange}
          />
          <StyledSwitchLabel>Show zero-balance accounts</StyledSwitchLabel>
        </Space>
      </Flex>
      <StyledTable
        loading={isLoading || isMultipleAccountsOnPaymentAuthorizedLoading}
        bordered
        rowSelection={{
          type: isMultipleAccountsOnPaymentAuthorized ? "checkbox" : "radio",
          ...rowSelection,
        }}
        // @ts-ignore
        columns={columns}
        dataSource={data}
      />
      <StyledAmountDueDiv>
        <span>Amount Due Across Selected Accounts: </span>
        <StyledValue>{hasSelected ? `${formatCurrency(debtTotal)}` : "$0.00"}</StyledValue>
        <div>
          <i>
            Convenience fees will not be applied if one or more accounts have disabled convenience
            fees.
          </i>
        </div>
      </StyledAmountDueDiv>
      <StyledDivider />
      <Form
        form={form}
        labelAlign="left"
        layout="vertical"
        initialValues={initialValues}
        validateMessages={{ required: "This is a required field" }}
        onFinish={onFinish}
      >
        <PaymentConfiguration />
        <Row>
          <Form.Item>
            <Tooltip title={isSelectionValid ? "" : selectionTooltipMessage}>
              <Button type="primary" htmlType="submit" disabled={!isSelectionValid}>
                Continue
              </Button>
            </Tooltip>
          </Form.Item>
        </Row>
      </Form>
    </div>
  );
}

export default SelectAccountsForPayment;
